import React, { Fragment, useEffect, useRef, useState } from "react";
import useNavHighlight from "../../hooks/useNavHighlight";
import usePageMeta from "../../hooks/usePageMeta";
import useScannerBuffer from "../../hooks/useScannerBuffer";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  saveUpcScanAction,
  exportUpcScanExcelAction
} from "../../redux/actions/upcScanActions";
import { UPC_SCAN_SAVE_RESET } from "../../redux/constants/upcScanConstants";
import { clearErrors } from "../../redux/actions/userActions";
import Loader from "../loader/loader";
import api from "../../api";
import ErrorAudio from "../images/error-audio.mp3";
import ScanInputSection from "./ScanInputSection";
import ScannedItemsTable from "./ScannedItemsTable";
import WrongScansTable from "./WrongScansTable";
import normalizeUpc from "../../utils/normalizeUpc";
import { reasonToErrorType } from "../../constants/scanValidation";

const Dashboard = () => {

  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const scanQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const scanModeRef = useRef("case");
  const inventoryModeRef = useRef(false);
  const resolveCacheRef = useRef(new Map());

  const [products, setProducts] = useState([]);
  const [wrongScans, setWrongScans] = useState([]);
  const [scanMode, setScanMode] = useState("case");
  const [inventoryMode, setInventoryMode] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  const { loading, error, message } = useSelector(state => state.upcScan);

  const errorSoundRef = useRef(new Audio(ErrorAudio));

  useNavHighlight("dashboard");
  usePageMeta("dashboard");

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const clearResolveCache = () => {
    resolveCacheRef.current.clear();
  };

  useEffect(() => {
    scanModeRef.current = scanMode;
  }, [scanMode]);

  useEffect(() => {
    inventoryModeRef.current = inventoryMode;
    clearResolveCache();
  }, [inventoryMode]);

  const commitWrongScan = (upc, {
    quantity = 1,
    description = null,
    errorType = null,
    invalidReason = null,
  } = {}) => {
    errorSoundRef.current.play();
    setWrongScans(prev => {
      const existing = prev.find(item => item.upc === upc);
      if (existing) {
        return [{
          ...existing,
          quantity: existing.quantity + quantity,
          description: existing.description || description,
          errorType: existing.errorType || errorType,
          invalidReason: existing.invalidReason || invalidReason,
        }, ...prev.filter(item => item.upc !== upc)];
      }
      return [{ upc, quantity, description, errorType, invalidReason }, ...prev];
    });
  };

  const fetchResolve = async (upc, inventoryOn) => {
    const cacheKey = `${inventoryOn ? "ws" : "map"}:${upc}`;
    if (resolveCacheRef.current.has(cacheKey)) {
      return resolveCacheRef.current.get(cacheKey);
    }
    try {
      const sourceParam = inventoryOn ? "&source=wholesale" : "";
      const { data } = await api.get(`/api/v1/mapping/resolve-scan?upc=${upc}${sourceParam}`);
      const result = data.success ? data : null;
      resolveCacheRef.current.set(cacheKey, result);
      return result;
    } catch {
      resolveCacheRef.current.set(cacheKey, null);
      return null;
    }
  };

  const processOneScan = async (originalValue, mode, inventoryOn) => {
    const upc = normalizeUpc(originalValue);

    if (!upc) {
      const raw = String(originalValue || "").replace(/\s/g, "");
      commitWrongScan(originalValue, {
        quantity: 1,
        errorType: /^\d+$/.test(raw) ? "INVALID_LENGTH" : "INVALID_CHARACTERS",
        invalidReason: /^\d+$/.test(raw) ? "INVALID_LENGTH" : "INVALID_CHARACTERS",
      });
      return;
    }

    const resolved = await fetchResolve(upc, inventoryOn);
    if (!resolved?.valid) {
      let qty = 1;
      if (mode === "case") {
        if (resolved?.mapping?.unit_case) {
          qty = Number(resolved.mapping.unit_case) || 1;
        } else if (resolved?.masterUnitCase) {
          qty = Number(resolved.masterUnitCase) || 1;
        }
      } else if (inventoryOn && resolved?.mapping?.carton_quantity) {
        qty = Number(resolved.mapping.carton_quantity) || 1;
      }
      commitWrongScan(resolved?.lineItemUpc || upc, {
        quantity: qty,
        description: resolved?.masterDescription || null,
        invalidReason: resolved?.invalidReason,
        errorType: reasonToErrorType(resolved?.invalidReason),
      });
      return;
    }

    const { mapping, description, lineItemUpc } = resolved;
    let finalQuantity = 1;
    let mappingParentBarcode = null;

    if (mapping) {
      const { unit_case, match_type, parent_barcode, child_barcode } = mapping;
      mappingParentBarcode = parent_barcode;

      const sameBarcode = parent_barcode === child_barcode;
      const eachQty = inventoryOn
        ? (Number(mapping.carton_quantity) || 1)
        : 1;

      if (sameBarcode) {
        finalQuantity = mode === "case" ? (Number(unit_case) || 1) : eachQty;
      } else if (match_type === "parent" || mode === "case") {
        finalQuantity = Number(unit_case) || 1;
      } else {
        finalQuantity = eachQty;
      }
    }

    const desc = description || "Not added";

    setProducts(prev => {
      const existing = prev.find(p =>
        p.upc === lineItemUpc ||
        (mappingParentBarcode && p.upc === mappingParentBarcode)
      );
      if (existing) {
        return [
          {
            upc: lineItemUpc,
            quantity: existing.quantity + finalQuantity,
            description: existing.description !== "Not added" ? existing.description : desc,
          },
          ...prev.filter(p =>
            p.upc !== lineItemUpc &&
            p.upc !== mappingParentBarcode
          ),
        ];
      }
      return [{ upc: lineItemUpc, quantity: finalQuantity, description: desc }, ...prev];
    });
  };

  const drainScanQueue = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      while (scanQueueRef.current.length > 0) {
        const { raw, mode, inventoryOn } = scanQueueRef.current.shift();
        setQueueSize(scanQueueRef.current.length);
        await processOneScan(raw, mode, inventoryOn);
      }
    } finally {
      isProcessingRef.current = false;
      setQueueSize(0);
      inputRef.current?.focus();
    }
  };

  const enqueueScan = (raw) => {
    scanQueueRef.current.push({
      raw,
      mode: scanModeRef.current,
      inventoryOn: inventoryModeRef.current,
    });
    setQueueSize(scanQueueRef.current.length);
    drainScanQueue();
  };

  useScannerBuffer({
    enabled: true,
    inputRef,
    onScan: enqueueScan,
  });

  const handleRemove = (upc) => {
    setProducts(prev =>
      prev
        .map(p =>
          p.upc === upc
            ? { ...p, quantity: p.quantity - 1 }
            : p
        )
        .filter(p => p.quantity > 0)
    );
  };

  const handleSetQuantity = (upc, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    setProducts(prev =>
      prev.map(p =>
        p.upc === upc ? { ...p, quantity: qty } : p
      )
    );
  };

  const handleWrongSetQuantity = (upc, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    setWrongScans(prev =>
      prev.map(p =>
        p.upc === upc ? { ...p, quantity: qty } : p
      )
    );
  };

  const handleWrongRemove = (upc) => {
    setWrongScans(prev =>
      prev
        .map(p =>
          p.upc === upc
            ? { ...p, quantity: p.quantity - 1 }
            : p
        )
        .filter(p => p.quantity > 0)
    );
  };

  const handleSave = () => {
    if (products.length === 0 && wrongScans.length === 0) return;

    dispatch(saveUpcScanAction({
      products,
      wrongScans
    }));

    setProducts([]);
    setWrongScans([]);
    clearResolveCache();
  };

  const handleExport = () => {
    if (products.length === 0 && wrongScans.length === 0) return;

    dispatch(exportUpcScanExcelAction({
      products,
      wrongScans
    }));

    setProducts([]);
    setWrongScans([]);
    clearResolveCache();
  };

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch({ type: UPC_SCAN_SAVE_RESET });
      setProducts([]);
      setWrongScans([]);
      clearResolveCache();
    }

    if (error) {
      toast.error(error);
      dispatch(clearErrors());
    }
  }, [error, message, dispatch]);

  const isEmpty = products.length === 0 && wrongScans.length === 0;

  return (
    <Fragment>

      {loading && <Loader />}

      <section className="ml-[1.5vmax] mr-[2.5vmax] flex flex-col gap-4">

        <div className="bg-white rounded-2xl shadow-1 p-[2vmax]">
          <ScanInputSection
            inputRef={inputRef}
            scanMode={scanMode}
            setScanMode={setScanMode}
            inventoryMode={inventoryMode}
            setInventoryMode={setInventoryMode}
            onSubmit={enqueueScan}
          />
          {queueSize > 0 && (
            <p className="text-center text-[13px] text-blue-600 mt-2">
              Processing {queueSize} scan{queueSize !== 1 ? "s" : ""} in queue…
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-1 p-[2vmax]">
          <div className="flex items-center gap-2 mb-4">
            <i className="fa-solid fa-check-circle text-green-500 text-[18px]" />
            <h2 className="text-[16px] font-bold text-gray-700">Scanned Items
              {products.length > 0 && (
                <span className="ml-2 bg-green-100 text-green-700 text-[12px] font-semibold px-2 py-0.5 rounded-full">
                  {products.length}
                </span>
              )}
            </h2>
          </div>
          <ScannedItemsTable
            products={products}
            handleRemove={handleRemove}
            handleSetQuantity={handleSetQuantity}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-1 p-[2vmax]">
          <div className="flex items-center gap-2 mb-4">
            <i className="fa-solid fa-circle-xmark text-red-500 text-[18px]" />
            <h2 className="text-[16px] font-bold text-gray-700">Invalid Scans
              {wrongScans.length > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 text-[12px] font-semibold px-2 py-0.5 rounded-full">
                  {wrongScans.length}
                </span>
              )}
            </h2>
          </div>
          <WrongScansTable
            wrongScans={wrongScans}
            handleWrongRemove={handleWrongRemove}
            handleWrongSetQuantity={handleWrongSetQuantity}
          />
        </div>

        <div className="flex justify-end items-center gap-3 pb-[2vmax]">
          <button
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-[14px] bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors ${isEmpty && "opacity-40 cursor-not-allowed"}`}
            onClick={() => { setProducts([]); setWrongScans([]); }}
            disabled={isEmpty}
          >
            <i className="fa-solid fa-xmark" /> Cancel
          </button>

          <button
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-[14px] bg-blue-600 hover:bg-blue-700 text-white transition-colors ${isEmpty && "opacity-40 cursor-not-allowed"}`}
            onClick={() => handleSave()}
            disabled={isEmpty}
          >
            <i className="fa-solid fa-floppy-disk" /> Save
          </button>

          <button
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-[14px] bg-green-600 hover:bg-green-700 text-white transition-colors ${isEmpty && "opacity-40 cursor-not-allowed"}`}
            onClick={handleExport}
            disabled={isEmpty}
          >
            <i className="fa-solid fa-file-excel" /> Export Excel
          </button>
        </div>

      </section>

    </Fragment>
  );
};

export default Dashboard;
